import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import './About.css';
import './Home.css';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Seo from '../components/Seo';
import AOS from 'aos';
import 'aos/dist/aos.css';
import WaveTop from '../components/WaveTop';
import WaveBottom from '../components/WaveBottom';
import SampleModal from '../components/samples/SampleModal';

const pillarIcons = [
  'fas fa-heart',
  'fas fa-handshake',
  'fas fa-hand-holding-heart',
  'fas fa-star',
  'fas fa-scale-balanced',
  'fas fa-lightbulb',
  'fas fa-seedling',
  'fas fa-globe',
  'fas fa-bullseye',
];

function About() {
  const { t } = useTranslation();
  const [showModal, setShowModal] = useState(false);
  const [activeSample, setActiveSample] = useState(null);
  useEffect(() => {
    AOS.init({ duration: 1000 });
  }, []);

  const pillars = pillarIcons.map((icon, index) => {
    const n = index + 1;
    return {
      month: t(`scopeSequence.month_${n}`),
      icon,
      name: t(`scopeSequence.pillar_${n}`),
      focus: t(`home.pillar_focus_${n}`),
    };
  });


  return (
    <>
      <Seo
        title="Character Education in Action"
        description="MoralEd brings monthly values-based character education to K-12 schools in English, Russian, and Kyrgyz. Discover our program."
        path="/"
      />
      <Header />

      <main className="home">
        {/* HERO SECTION */}
        <section className="hero">
          <video
            autoPlay
            muted
            loop
            className="hero-video"
            aria-label="Background video showing children learning"
          >
            <source src="/videos/hero.mp4" type="video/mp4" />
            Your browser does not support the video tag.
          </video>

          <div className="hero-overlay" data-aos="fade-up">
            <h1>{t('home.hero_title')}</h1>
            <p>{t('home.hero_subtitle')}</p>
            <button
              className="btn-primary btn-hero"
              onClick={() => {
                document.querySelector('.values')?.scrollIntoView({ behavior: 'smooth' });
              }}
            >
              {t('home.hero_button')}
            </button>
          </div>
        </section>

        {/* DEFINITION */}
        <section className="definition-section" data-aos="fade-up">
          <div className="definition-wrapper">
            <h2>{t('definition.title')}</h2>
            <p className="definition-text">{t('definition.text')}</p>
            <p className="definition-followup">{t('definition.followup')}</p>
          </div>
        </section>

        {/* CORE VALUES */}
        <section className="core-values" data-aos="fade-up">
          <h2>{t('home.core_values_title')}</h2>
          <p className="core-values-sub">{t('home.core_values_subtitle')}</p>
          <div className="core-values-grid">
            {pillars.map((p) => (
              <div className="value-card" key={p.month}>
                <i className={`${p.icon} fa-2x`}></i>
                <span className="value-card__month">{p.month}</span>
                <h3>{p.name}</h3>
                <p>{p.focus}</p>
              </div>
            ))}
          </div>
        </section>

        {/* OUR FRAMEWORK */}
        <section className="about-section framework-section" data-aos="fade-up">
          <div className="framework-wrapper">
            <h2>{t('about.framework_title')}</h2>
            <div className="framework-grid">
              <div className="framework-card">
                <span className="framework-icon"><i className="fas fa-child"></i></span>
                <h3>{t('about.framework_1_title')}</h3>
                <p>{t('about.framework_1_desc')}</p>
              </div>
              <div className="framework-card">
                <span className="framework-icon"><i className="fas fa-lightbulb"></i></span>
                <h3>{t('about.framework_2_title')}</h3>
                <p>{t('about.framework_2_desc')}</p>
              </div>
              <div className="framework-card">
                <span className="framework-icon"><i className="fas fa-palette"></i></span>
                <h3>{t('about.framework_3_title')}</h3>
                <p>{t('about.framework_3_desc')}</p>
              </div>
              <div className="framework-card">
                <span className="framework-icon"><i className="fas fa-handshake"></i></span>
                <h3>{t('about.framework_4_title')}</h3>
                <p>{t('about.framework_4_desc')}</p>
              </div>
              <div className="framework-card">
                <span className="framework-icon"><i className="fas fa-chart-line"></i></span>
                <h3>{t('about.framework_5_title')}</h3>
                <p>{t('about.framework_5_desc')}</p>
              </div>
            </div>
          </div>
        </section>

        {/* QUOTE SECTION */}
        <section className="quote-section" data-aos="fade-up">
          <WaveTop />
          <div className="quote-background-pattern"
            style={{
              backgroundImage: "url('/patterns/texture-light.svg')",
              backgroundRepeat: "repeat",
              opacity: 0.08,
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              zIndex: 0,
            }}
          ></div>
          <div className="quote-content">
            <h3>
              <span className="quote-highlight">"{t('home.quote_highlight')}"</span><br />
            </h3>
            <h4>
              <span className="quote-sub">{t('home.quote_subtitle')}</span>
            </h4>
            <button className="btn-primary" onClick={() => setShowModal(true)}>
              {t('home.quote_button')}
            </button>
          </div>
          <WaveBottom />
        </section>
        {showModal && (
          <div className="modal-backdrop" onClick={() => setShowModal(false)}>
            <div className="modal" onClick={(e) => e.stopPropagation()}>
              <h3>{t('home.modal_title')}</h3>
              <p>{t('home.modal_text')}</p>
              <button onClick={() => setShowModal(false)}>{t('home.modal_close')}</button>
            </div>
          </div>
        )}

        {/* SAMPLE RESOURCES */}
        <section className="samples" data-aos="fade-up">
          <h2>{t('home.resources_title')}</h2>
          <p className="samples-sub">{t('home.resources_subtitle')}</p>

          <div className="sample-grid">
            <div className="sample-card" data-aos="flip-left">
              <img src="/icons/story.png" alt="Story icon" />
              <h3>{t('home.story_title')}</h3>
              <p>{t('home.story_desc')}</p>
              <button className="btn-outline" onClick={() => setActiveSample('story')}>
                {t('home.story_button')}
              </button>
            </div>

            <div className="sample-card" data-aos="flip-left" data-aos-delay="100">
              <img src="/icons/song.png" alt="Song icon" />
              <h3>{t('home.song_title')}</h3>
              <p>{t('home.song_desc')}</p>
              <button className="btn-outline" onClick={() => setActiveSample('song')}>
                {t('home.song_button')}
              </button>
            </div>

            <div className="sample-card" data-aos="flip-left" data-aos-delay="200">
              <img src="/icons/game.png" alt="Game icon" />
              <h3>{t('home.game_title')}</h3>
              <p>{t('home.game_desc')}</p>
              <button className="btn-outline" onClick={() => setActiveSample('game')}>
                {t('home.game_button')}
              </button>
            </div>
          </div>
        </section>

        <SampleModal type={activeSample} onClose={() => setActiveSample(null)} />

        {/* WHOLE CHILD SUCCESS */}
        <section className="whole-child" data-aos="fade-up">
          <div className="whole-child-content">
            <h2>{t('home.whole_child_title')}</h2>
            <p>{t('home.whole_child_desc')}</p>

            <ul className="pillars">
              <li data-aos="fade-right"><i className="fas fa-brain"></i> {t('home.pillar_cognitive')}</li>
              <li data-aos="fade-right" data-aos-delay="100"><i className="fas fa-heart"></i> {t('home.pillar_emotional')}</li>
              <li data-aos="fade-right" data-aos-delay="200"><i className="fas fa-people-group"></i> {t('home.pillar_social')}</li>
              <li data-aos="fade-right" data-aos-delay="300"><i className="fas fa-leaf"></i> {t('home.pillar_moral')}</li>
            </ul>

            <div className="whole-child-buttons">
              <a href="/scope-and-sequence" className="btn-primary">
                {t('home.whole_child_button_1')}
              </a>
              <button
                className="btn-outline"
                onClick={() => {
                  document.querySelector('.values')?.scrollIntoView({ behavior: 'smooth' });
                }}
              >
                {t('home.whole_child_button_2')}
              </button>
            </div>
          </div>

          <div className="whole-child-visual" data-aos="zoom-in">
            <img
              src="/images/whole-child-success.svg"
              alt="Whole child success illustration"
              className="animated-illustration"
            />
          </div>
        </section>

        {/* TESTIMONIALS */}
        <section className="testimonials" data-aos="fade-up">
          <h2>{t('home.testimonials_title')}</h2>
          <p className="testimonial-sub">{t('home.testimonials_subtitle')}</p>

          <div className="testimonial-grid">
            <div className="testimonial-card" data-aos="zoom-in">
              <p>"{t('home.testimonial_1')}"</p>
              <span>{t('home.testimonial_1_author')}</span>
            </div>
            <div className="testimonial-card" data-aos="zoom-in" data-aos-delay="100">
              <p>"{t('home.testimonial_2')}"</p>
              <span>{t('home.testimonial_2_author')}</span>
            </div>
            <div className="testimonial-card" data-aos="zoom-in" data-aos-delay="200">
              <p>"{t('home.testimonial_3')}"</p>
              <span>{t('home.testimonial_3_author')}</span>
            </div>
          </div>

          <div className="testimonial-actions">
            <a href="/contact" className="btn-outline">
              {t('home.testimonial_button')}
            </a>
          </div>
        </section>

      </main>

      <Footer />
    </>
  );
}

export default About;
